import React from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import { colors, fontStyles } from '../../styles';
import { calculateSavingsPercentage } from '../../utils/pricing';

interface PlanSelectionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedType: 'yearly' | 'weekly';
  onPlanSelect: (type: 'yearly' | 'weekly') => void;
  weeklyPrice: number;
  yearlyPrice: number;
  currencySymbol: string;
  freeTrialDays: number;
  onStartTrial: () => void;
  shouldShowDiscount?: boolean;
  originalYearlyPrice?: number;
  disabled?: boolean;
  buttonLabel?: string;
}

export const PlanSelectionSheet: React.FC<PlanSelectionSheetProps> = ({
  isOpen,
  onClose,
  selectedType,
  onPlanSelect,
  weeklyPrice,
  yearlyPrice,
  currencySymbol,
  freeTrialDays,
  onStartTrial,
  shouldShowDiscount = false,
  originalYearlyPrice,
  disabled = false,
  buttonLabel,
}) => {
  const { t } = useTranslation();
  const yearlyWeeklyPrice = Math.round((yearlyPrice / 52) * 100) / 100;
  // When a discount is active, compare the discounted price against the original yearly price
  // (not against weekly * 52) so the badge matches what the user sees visually.
  const savingsPercentage =
    shouldShowDiscount && originalYearlyPrice && originalYearlyPrice > 0
      ? Math.round((1 - yearlyPrice / originalYearlyPrice) * 100)
      : calculateSavingsPercentage(weeklyPrice, yearlyPrice);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('subscription.chooseAPlan')} headerStyle="left-aligned">
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        {/* Payment plans */}
        <div
          css={css`
            display: flex;
            gap: 16px;
            margin-bottom: 16px;
          `}
        >
          {/* Weekly plan */}
          <div
            css={css`
              display: flex;
              flex: 1;
              border-radius: 16px;
              border: 3px solid ${selectedType === 'weekly' ? colors.petrolBlue : '#E5E5E5'};
              background: ${selectedType === 'weekly' ? '#F0F9F9' : 'white'};
              padding: 20px;
              cursor: pointer;
              transition: all 0.2s ease;
              position: relative;
              justify-content: space-between;
              align-items: center;
            `}
            onClick={() => {
              // Track plan change event
              if (selectedType !== 'weekly') {
                try {
                  posthog.capture('Plan Changed', {
                    previous_plan: selectedType,
                    new_plan: 'weekly',
                    weekly_price: weeklyPrice,
                    yearly_price: yearlyPrice,
                    savings_percentage: savingsPercentage,
                    currency_symbol: currencySymbol,
                  });
                } catch (error) {
                  console.error('Failed to track plan change event:', error);
                }
              }
              onPlanSelect('weekly');
            }}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              `}
            >
              <h3
                css={css`
                  ${fontStyles.lion};
                  color: ${colors.liquorice};
                  margin: 0 0 4px 0;
                `}
              >
                {t('subscription.weekly')}
              </h3>
              <p
                css={css`
                  margin: 0;
                  ${fontStyles.cat};
                  color: ${colors.liquorice};
                `}
              >
                {t('subscription.pricePerWeek', { price: `${currencySymbol}${weeklyPrice.toFixed(2)}` })}
              </p>
            </div>

            <div
              css={css`
                width: 28px;
                height: 28px;
                border: 2px solid ${selectedType === 'weekly' ? colors.petrolBlue : '#E5E5E5'};
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: ${selectedType === 'weekly' ? colors.petrolBlue : 'white'};
              `}
            >
              {selectedType === 'weekly' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                </svg>
              )}
            </div>
          </div>

          {/* Yearly plan */}
          <div
            css={css`
              display: flex;
              flex: 1;
              border-radius: 16px;
              border: 3px solid ${selectedType === 'yearly' ? colors.petrolBlue : '#E5E5E5'};
              background: ${selectedType === 'yearly' ? '#F0F9F9' : 'white'};
              padding: 20px;
              cursor: pointer;
              transition: all 0.2s ease;
              position: relative;
              justify-content: space-between;
              align-items: center;
            `}
            onClick={() => {
              // Track plan change event
              if (selectedType !== 'yearly') {
                try {
                  posthog.capture('Plan Changed', {
                    previous_plan: selectedType,
                    new_plan: 'yearly',
                    weekly_price: weeklyPrice,
                    yearly_price: yearlyPrice,
                    savings_percentage: savingsPercentage,
                    currency_symbol: currencySymbol,
                  });
                } catch (error) {
                  console.error('Failed to track plan change event:', error);
                }
              }
              onPlanSelect('yearly');
            }}
          >
            {/* SAVE badge */}
            <div
              css={css`
                position: absolute;
                top: -12px;
                left: 0;
                right: 0;
                display: flex;
                justify-content: center;
                z-index: 10;
              `}
            >
              <div
                css={css`
                  background-color: ${colors.petrolBlue};
                  color: white;
                  ${fontStyles.mouse};
                  padding: 4px 20px 6px;
                  border-radius: 12px;
                  text-transform: uppercase;
                `}
              >
                {t('subscription.savePercent', { percent: savingsPercentage })}
              </div>
            </div>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              `}
            >
              <h3
                css={css`
                  margin: 0 0 4px 0;
                  ${fontStyles.lion};
                  color: ${colors.liquorice};
                `}
              >
                {t('subscription.yearly')}
              </h3>
              <p
                css={css`
                  margin: 0;
                  ${fontStyles.cat};
                  color: ${colors.liquorice};
                `}
              >
                {t('subscription.pricePerWeek', { price: `${currencySymbol}${yearlyWeeklyPrice}` })}
              </p>
            </div>

            <div
              css={css`
                width: 28px;
                height: 28px;
                border: 2px solid ${selectedType === 'yearly' ? colors.petrolBlue : '#E5E5E5'};
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: ${selectedType === 'yearly' ? colors.petrolBlue : 'white'};
              `}
            >
              {selectedType === 'yearly' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic text based on selection */}
        <p
          css={css`
            ${fontStyles.cat}
            color: #666;
            text-align: center;
            margin: 0 0 24px 0;
            font-size: 16px;
          `}
        >
          {selectedType === 'weekly' ? (
            t('subscription.weeklyPriceCancel', { price: `${currencySymbol}${weeklyPrice.toFixed(2)}` })
          ) : shouldShowDiscount && originalYearlyPrice ? (
            <>
              {t('subscription.daysFreeThen', { days: freeTrialDays })}{' '}
              <span
                css={css`
                  text-decoration: line-through;
                  opacity: 0.6;
                  margin-right: 4px;
                `}
              >
                {currencySymbol}
                {originalYearlyPrice}
              </span>
              {t('subscription.perYear', { price: `${currencySymbol}${yearlyPrice}` })}
            </>
          ) : (
            t('subscription.daysFreeThenPrice', { days: freeTrialDays, price: `${currencySymbol}${yearlyPrice}` })
          )}
        </p>

        {/* Start trial button */}
        <Button
          variant="primary"
          block
          onClick={onStartTrial}
          disabled={disabled}
          css={css`
            ${fontStyles.horse}
            padding: 18px 24px;
            border-radius: 12px;
            min-height: 56px;
            background-color: ${colors.petrolBlue};
            color: white;
            border: none;
            cursor: pointer;
            width: 100%;
            transition: all 0.2s ease;

            &:hover {
              opacity: 0.9;
              transform: translateY(-1px);
            }
          `}
        >
          {buttonLabel ||
            (selectedType === 'weekly' ? t('subscription.subscribeToBloom') : t('subscription.startFreeTrial'))}
        </Button>
      </div>
    </BottomSheet>
  );
};
